import React, { useEffect, useState, useRef } from "react";
import {
  Card, CardBody, Row, Col, Input, Button, Table, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter
} from "reactstrap";
import AsyncSelect from "react-select/async";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import { getCookie, reloadWindow, textCapitalize } from "../../Helpers/Utils";
import { useParams } from "react-router-dom";

const POS = () => {

  const dispatch = useDispatch();
  const user = useSelector(s => s.customer.customer);
  const loading = useSelector(s => s.loading.loading);
  const [customerMenuOpen, setCustomerMenuOpen] = useState(false);
  const { id, payid } = useParams();
  const customerSelectRef = useRef(null);
  const paymentInputRef = useRef(null);

  /* ================= FIXED STATES ================= */
  const [newCustomer, setNewCustomer] = useState({ name: "", phone: "" });
  const [loyaltyPoints, setLoyaltyPoints] = useState(0); // UI compatibility

  const [discountValue, setDiscountValue] = useState(0); // The raw number entered (e.g., 10)
  const [discountType, setDiscountType] = useState("FLAT"); // Either "FLAT" (₹) or "PERCENTAGE" (%)

  /* ================= PRODUCTS ================= */
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");

  /* ================= CART ================= */
  const [cart, setCart] = useState([]);

  /* ================= CUSTOMER ================= */
  const [customerOption, setCustomerOption] = useState(null);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loyaltyBalance, setLoyaltyBalance] = useState(0);

  /* ================= SALE / PAYMENT ================= */
  const [saleData, setSaleData] = useState(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentMode, setPaymentMode] = useState("CASH");
  const [manualDiscount, setManualDiscount] = useState(0);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [showBillBtns, setShowBillBtns] = useState(false);
  const [paymentId, setPaymentId] = useState();

  /* ================= BARCODE ================= */
  const barcodeBuffer = useRef("");
  const barcodeTimer = useRef(null);

  /* =================================================
     LOAD PRODUCTS
  ================================================= */

  const updateQty = (key, qty) => {
    if (qty <= 0) setCart(c => c.filter(i => i.key !== key));
    else setCart(c => c.map(i => i.key === key ? { ...i, qty } : i));
  };

  const loadProducts = async () => {
    dispatch(loadingTrue());
    const res = await Handler({
      method: "get",
      url: `/products/search.php?outlet_id=${user.outlet_id}&q=${search}`
    });
    if (res?.success) setProducts(res.data || []);
    dispatch(loadingFalse());
  };

  useEffect(() => { loadProducts(); }, [search]);

  /* =================================================
     LOAD SALE (EDIT)
  ================================================= */

  useEffect(() => {
    if (!id) return;
    loadSaleView();
  }, [id]);

  const loadSaleView = async () => {
    dispatch(loadingTrue());
    const res = await Handler({ method: "get", url: `/sales/view.php?id=${id}` });
    dispatch(loadingFalse());
    if (!res?.success) return toast.error("Failed to load sale");

    const { sale, items, loyalty, payment } = res.data;

    setPaymentId(payment?.payment_id)

    if (sale?.customer) {
      const opt = {
        value: sale.customer.id,
        label: `${sale.customer.name} - ${sale.customer.phone}`
      };

      setCustomerOption(opt);      // ✅ AsyncSelect shows selected
      setCustomerInfo(sale.customer);

      // 🔥 loyalty fetch mattum pannum
      const lp = await Handler({
        method: "post",
        url: `/loyalty_points/balance.php`,
        data: {
          customer_id: sale.customer.id,
          outlet_id: user.outlet_id
        }
      });

      if (lp?.success) {
        setLoyaltyBalance(lp.data.balance || 0);
        setLoyaltyPoints(lp.data.balance || 0);
      }
    }


    // const cus = await Handler({
    //   method: "post",
    //   url: `/customers/view.php`,
    //   data: { id: sale?.customer?.customer_id }
    // });

    // if (cus?.success) {
    //   setCustomerInfo(cus.data);
    //   setCustomerOption({
    //     value: cus.data.id,
    //     label: `${cus.data.name} - ${cus.data.phone}`
    //   });
    // }

    setCart(items.map(i => ({
      key: i.variant_id ? `v-${i.variant_id}` : `p-${i.product_id}`,
      product_id: i.product_id,
      variant_id: i.variant_id,
      name: i.variant_name
        ? `${textCapitalize(i.product_name)} (${textCapitalize(i.variant_name)})`
        : textCapitalize(i.product_name),
      qty: i.quantity,
      price: i.rate,
      gst_rate: i.gst_rate
    })));

    setCart(items.map(i => ({
      key: i.variant_id ? `v-${i.variant_id}` : `p-${i.product_id}`,
      product_id: i.product_id,
      variant_id: i.variant_id,
      name: i.variant_name
        ? `${textCapitalize(i.product_name)} (${textCapitalize(i.variant_name)})`
        : textCapitalize(i.product_name),
      qty: Number(i.quantity),
      price: Number(i.final_rate ?? i.rate ?? 0),
      gst_rate: Number(i.gst_rate ?? 0)
    })));


    setSaleData({ sale_id: sale.id });
    setLoyaltyBalance(loyalty?.points_earned || 0);
    setLoyaltyPoints(loyalty?.points_earned || 0); // UI alias
  };

  /* =================================================
     BARCODE
  ================================================= */
  useEffect(() => {
    const handler = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement.tagName)) return;

      if (e.key === "Enter") {
        if (barcodeBuffer.current.length >= 4) handleBarcode(barcodeBuffer.current);
        barcodeBuffer.current = "";
        return;
      }

      if (e.key.length === 1) barcodeBuffer.current += e.key;

      clearTimeout(barcodeTimer.current);
      barcodeTimer.current = setTimeout(() => barcodeBuffer.current = "", 120);
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [products]);

  const handleBarcode = (code) => {
    for (const p of products) {
      if (p.meta?.barcode === code) return addToCart(p);
      for (const v of p.variants || []) {
        if (v.barcode === code) return addToCart(p, v);
      }
    }
    toast.error("Barcode not found");
  };

  /* =================================================
     CART
  ================================================= */
  const addToCart = (p, v = null) => {
    const key = v ? `v-${v.variant_id}` : `p-${p.product_id}`;
    setCart(c => {
      const e = c.find(i => i.key === key);
      if (e) return c.map(i => i.key === key ? { ...i, qty: i.qty + 1 } : i);
      return [...c, {
        key,
        product_id: p.product_id,
        variant_id: v?.variant_id || null,
        name: v ? `${p.name} (${v.name})` : p.name,
        qty: 1,
        price: v ? v.discounted_price : p.discounted_price,
        gst_rate: v ? v.gst_rate : p.gst_rate
      }];
    });
  };

  const removeFromCart = (key) => {
    setCart(c => c.filter(i => i.key !== key));
  };

  /* =================================================
     TOTALS
  ================================================= */
  const cartSubtotal = cart.reduce((s, i) => s + (i.price * i.qty), 0);
  const cgstTotal = cart.reduce((s, i) => s + ((i.price * i.qty) * (i.gst_rate / 2)) / 100, 0);
  const sgstTotal = cgstTotal;
  // const grandTotal = cartSubtotal + cgstTotal + sgstTotal - Number(manualDiscount || 0);

  const calculatedManualDiscount = discountType === "PERCENTAGE"
    ? (cartSubtotal * Number(discountValue || 0)) / 100  // If %, calculate portion of subtotal
    : Number(discountValue || 0);                       // If FLAT, use the number directly

  // Applying it to the Grand Total
  const grandTotal = cartSubtotal + cgstTotal + sgstTotal - calculatedManualDiscount;

  /* =================================================
     UPDATE SALE (ALIAS FIX)
  ================================================= */
  const updateSale = async () => {
    if (!cart.length) return toast.error("Cart empty");
    if (!customerOption) return toast.error("Select customer");

    dispatch(loadingTrue());
    const res = await Handler({
      method: "post",
      url: "/sales/update.php",
      data: {
        sale_id: id,
        outlet_id: user.outlet_id,
        customer_id: customerOption.value,
        items: cart.map(i => ({
          product_id: i.product_id,
          variant_id: i.variant_id,
          quantity: i.qty,
          rate: i.price
        }))
      }
    });
    dispatch(loadingFalse());

    if (!res?.success) return toast.error(res?.msg);
    setPaymentOpen(true);
  };

  const createSale = updateSale; // 🔥 UI COMPATIBILITY

  /* =================================================
     UPDATE PAYMENT (ALIAS FIX)
  ================================================= */

  const updatePayment = async () => {

    if (!id) {
      toast.error("Sale not ready");
      return;
    }

    dispatch(loadingTrue());

    const res = await Handler({
      method: "post",
      url: "/payments/create.php",
      data: {
        sale_id: id,     // ✅ CORRECT
        outlet_id: user.outlet_id,     // ✅ REQUIRED
        payment_mode: paymentMode,
        meta: {
          redeem_points: redeemPoints,
          manual_discount: Number(manualDiscount) || 0
        }
      }
    });

    dispatch(loadingFalse());

    if (!res?.success) {
      toast.error(res?.msg);
      return;
    }

    setPaymentOpen(false);
    setShowBillBtns(true);

    // 🔥 auto thermal print
    setTimeout(() => {
      openPdf("https://whatsapp.billpro360.com/bill/apis/invoices/receipt.php");
      // openPdf("http://localhost/bill_backend_production/apis/invoices/receipt.php");
      // openPdf("http://localhost/billing-with-quotation/apis/invoices/receipt.php");
    }, 300);
  };



  const createPayment = updatePayment; // 🔥 UI COMPATIBILITY

  /* =================================================
     PDF
  ================================================= */
  const openPdf = (url) => {
    const token = getCookie("token");
    if (!token) return toast.error("Login expired");

    const w = window.open("", "_blank");
    w.document.write("Generating...");

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        sale_id: id,
        outlet_id: user.outlet_id
      })
    })
      .then(r => r.blob())
      .then(b => w.location.href = URL.createObjectURL(b))
      .catch(() => toast.error("Unauthorized"));
  };

  /* =================================================
     SHORTCUTS
  ================================================= */
  useEffect(() => {
    const handler = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement.tagName)) return;

      if (e.key === "F2") customerSelectRef.current?.focus();
      if (e.key === "F9") updateSale();

      if (paymentOpen) {
        if (e.key === "1") setPaymentMode("CASH");
        if (e.key === "2") setPaymentMode("UPI");
        if (e.key === "3") setPaymentMode("CARD");
        if (e.key === "F10") updatePayment();
      }

      if (e.key === "Escape") reloadWindow();
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [cart, paymentOpen, customerOption]);
  /* =================================================
   CUSTOMER SEARCH
================================================= */
  const searchCustomers = async (input) => {
    const res = await Handler({
      method: "get",
      url: `/customers/search_cus.php?q=${input || ""}&outlet_id=${user.outlet_id}`
    });

    if (!res?.success) return [];

    return res.data.map(c => ({
      value: c.id,
      label: `${c.name} - ${c.phone}`
    }));
  };



  /* =================================================
     CUSTOMER SELECT
  ================================================= */
  const selectCustomer = async (opt) => {

    if (!opt) {
      setCustomerOption(null);
      setCustomerInfo(null);
      setLoyaltyBalance(0);
      setRedeemPoints(0);
      return;
    }

    setCustomerOption(opt);

    // fetch full customer
    const res = await Handler({
      method: "post",
      url: `/customers/view.php`,
      data: { id: opt.value }
    });

    if (res?.success) setCustomerInfo(res.data);

    // fetch loyalty balance
    const lp = await Handler({
      method: "post",
      url: `/loyalty_points/balance.php`,
      data: { customer_id: opt.value, outlet_id: user.outlet_id }
    });

    if (lp?.success) {
      setLoyaltyBalance(lp.data.balance || 0);
      setLoyaltyPoints(lp.data.balance || 0); // UI alias safe
    }
  };

  const autoSelectFirstCustomer = async () => {
    const res = await Handler({
      method: "get",
      url: `/customers/search_cus.php?q=&outlet_id=${user.outlet_id}`
    });

    if (!res?.success || !res.data?.length) return;

    const first = {
      value: res.data[0].id,
      label: `${res.data[0].name} - ${res.data[0].phone}`
    };

    setCustomerOption(first);
    selectCustomer(first); // 🔥 THIS IS MUST
  };


  useEffect(() => {
    const handler = (e) => {

      // 🔒 BLOCK browser default for function keys
      if (["F2", "F9", "F10"].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
      }

      const isTyping =
        ["INPUT", "TEXTAREA"].includes(document.activeElement.tagName);

      /* ================= BARCODE ================= */
      if (!isTyping) {
        if (e.key === "Enter" && barcodeBuffer.current.length >= 4) {
          handleBarcode(barcodeBuffer.current);
          barcodeBuffer.current = "";
          return;
        }

        if (/^[a-zA-Z0-9]$/.test(e.key)) {
          barcodeBuffer.current += e.key;
          clearTimeout(barcodeTimer.current);
          barcodeTimer.current = setTimeout(() => {
            barcodeBuffer.current = "";
          }, 120);
        }
      }

      /* ================= F2 → CUSTOMER ================= */
      if (e.key === "F2" && !paymentOpen) {
        e.preventDefault();
        customerSelectRef.current?.focus();

        // 🔥 auto select first customer
        autoSelectFirstCustomer();
        return;
      }



      /* ================= F9 → CREATE SALE ================= */
      if (e.key === "F9" && !paymentOpen) {
        if (!cart.length) return toast.error("Cart empty");
        if (!customerOption) return toast.error("Select customer");
        updateSale();
        return;
      }

      /* ================= PAYMENT MODE ================= */
      if (paymentOpen) {
        if (e.key === "1") setPaymentMode("CASH");
        if (e.key === "2") setPaymentMode("UPI");
        if (e.key === "3") setPaymentMode("CARD");
      }

      /* ================= F10 → PAY ================= */
      if (e.key === "F10" && paymentOpen) {
        updatePayment();
        return;
      }

      /* ================= ESC ================= */
      if (e.key === "Escape") {
        reloadWindow();
      }
    };

    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);

  }, [
    cart,
    paymentOpen,
    customerOption,
    paymentMode,
    manualDiscount,
    redeemPoints
  ]);

  useEffect(() => {
    if (paymentOpen) {
      setTimeout(() => paymentInputRef.current?.focus(), 150);
    }
  }, [paymentOpen]);


  return (
    <>
      <Row><Col><h5 className="fw-bold">POS (Update)</h5></Col></Row>

      <Card className="mb-3 border-0 shadow-sm">
        <CardBody className="p-2">
          <div className="row g-2 small align-items-center">
            <div className="col-2 fw-bold"> Quick Keyboard Help :</div>
            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">F2</Badge> Customer</div>
            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">F9</Badge> Create Sale</div>

            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">F10</Badge> Pay</div>
            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">ESC</Badge> Cancel</div>

            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">1</Badge> Cash</div>
            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">2</Badge> UPI</div>

            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">3</Badge> Card</div>
            <div className="col-1 d-flex align-items-center gap-1"><Badge color="dark">Enter</Badge> Barcode</div>
          </div>
        </CardBody>
      </Card>


      <Row className="g-3">
        {/* LEFT */}
        <Col md={8}>
          <Input
            placeholder="Scan / Search product"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="mb-3"
          />

          <Card className="shadow-sm">
            <CardBody className="p-0">
              <Table hover responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Item</th>
                    <th>SKU / Barcode</th>
                    <th className="text-end">Stock</th>
                    <th className="text-end">Price</th>
                    <th className="text-end">Add</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(p => (
                    <React.Fragment key={p.product_id}>
                      <tr>
                        <td className="fw-bold">{p.name}</td>
                        <td className="small text-muted">
                          SKU: {p.meta?.sku || "-"}<br />
                          BC: {p.meta?.barcode || "-"}
                        </td>
                        <td className="text-end">
                          <Badge color={p.quantity > 0 ? "success" : "danger"}>
                            {p.quantity}
                          </Badge>
                        </td>
                        <td className="text-end fw-bold">₹ {Number(p.discounted_price).toFixed(2)}</td>
                        <td className="text-end">
                          <Button size="sm" disabled={p.quantity <= 0 || loading} color="primary" onClick={() => addToCart(p)}>+</Button>
                        </td>
                      </tr>

                      {p?.variants?.map(v => (
                        <tr key={v.variant_id} className="text-muted">
                          <td className="ps-4">↳ {v.name}</td>
                          <td className="small">
                            SKU: {v.sku || "-"}<br />
                            BC: {v.barcode || "-"}
                          </td>
                          <td className="text-end">{v.quantity}</td>
                          <td className="text-end">₹ {Number(v.discounted_price).toFixed(2)}</td>
                          <td className="text-end">
                            <Button size="sm" color="warning" disabled={v.quantity <= 0 || loading} onClick={() => addToCart(p, v)}>+</Button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>

        {/* RIGHT */}
        <Col md={4}>
          <Card className="shadow-sm">
            <CardBody>

              <Card className="mb-2">
                <CardBody className="p-2">
                  <Row className="g-2">
                    <Col xs={12}>
                      <Input
                        placeholder="Customer Name"
                        value={newCustomer.name}
                        onChange={(e) =>
                          setNewCustomer({ ...newCustomer, name: e.target.value })
                        }
                      />
                    </Col>
                    <Col xs={12}>
                      <Input
                        placeholder="Mobile Number"
                        maxLength={10}
                        value={newCustomer.phone}
                        onChange={(e) =>
                          setNewCustomer({ ...newCustomer, phone: e.target.value })
                        }
                      />
                    </Col>
                    <Col xs={12}>
                      <Button
                        disabled={loading}
                        color="success"
                        onClick={async () => {
                          if (!newCustomer.name || !newCustomer.phone)
                            return toast.error("Name & Mobile required");

                          const res = await Handler({
                            method: "post",
                            url: "/customers/add.php",
                            data: {
                              name: newCustomer.name,
                              phone: newCustomer.phone,
                              outlet_id: user.outlet_id
                            }
                          });

                          if (!res?.success) return toast.error(res?.msg);

                          // 🔥 AUTO SELECT NEW CUSTOMER
                          setCustomerOption({
                            value: res.data.id,
                            label: `${res.data.name} - ${res.data.phone}`
                          });

                          setCustomerInfo(res.data);
                          setNewCustomer({ name: "", phone: "" });

                          toast.success("Customer added");
                        }}
                      >
                        Add New Customer
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>


              <AsyncSelect
                ref={customerSelectRef}
                cacheOptions
                defaultOptions
                loadOptions={searchCustomers}
                value={customerOption}
                onChange={selectCustomer}
                placeholder="Search customer (Name / Mobile)"
                isClearable
              />
              {customerInfo && (
                <div className="mt-2 small text-muted">
                  <b>{customerInfo.name}</b><br />
                  {customerInfo.phone}<br />
                  <span className="text-success">
                    Loyalty Points: {loyaltyPoints}
                  </span>
                </div>
              )}

              <hr />

              {cart.map(i => {
                const itemTotal = i.price * i.qty;
                const halfGST = Number(i.gst_rate || 0) / 2;
                const cgst = (itemTotal * halfGST) / 100;
                const sgst = (itemTotal * halfGST) / 100;

                return (
                  <div key={i.key} className="mb-3">
                    <div className="d-flex justify-content-between">
                      <div>
                        <b>{textCapitalize(i.name)}</b>
                        <div className="small text-muted">
                          ₹{i.price} × {i.qty}
                          <span className="ms-2">(GST {i.gst_rate}%)</span>
                        </div>
                      </div>
                      <div className="fw-bold">
                        ₹ {itemTotal.toFixed(2)}
                      </div>
                      {/* 🗑 DELETE BUTTON */}
                      <Button disabled={loading} size="sm" color="danger" outline className="mt-1" onClick={() => updateQty(i.key, 0)} >
                        <i className='bx  bx-trash'></i>
                      </Button>
                    </div>

                    {/* GST SPLIT */}
                    {i.gst_rate > 0 && (
                      <div className="small text-muted ms-2 mt-1">
                        <div>CGST ({halfGST}%) : ₹ {cgst.toFixed(2)}</div>
                        <div>SGST ({halfGST}%) : ₹ {sgst.toFixed(2)}</div>
                      </div>
                    )}
                  </div>
                );
              })}


              <hr />
              <div className="d-flex justify-content-between">
                <span className="fw-bold">Grand Total</span>
                <span className="fw-bold fs-5">
                  ₹ {Number(grandTotal).toFixed(2)}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-1 small text-muted">
                <span>CGST</span>
                <span>₹ {cgstTotal.toFixed(2)}</span>
              </div>

              <div className="d-flex justify-content-between mb-1 small text-muted">
                <span>SGST</span>
                <span>₹ {sgstTotal.toFixed(2)}</span>
              </div>

              {!showBillBtns && (
                <Button disabled={loading} color="success" className="w-100" onClick={createSale}>
                  Update Sale
                </Button>
              )}

              {showBillBtns && (
                <>
                  <Button
                    disabled={loading}
                    color="primary"
                    className="w-100 mb-2"
                    onClick={() =>
                      openPdf(
                        // "http://localhost/bill_backend_production/apis/invoices/receipt.php"
                        // "http://localhost/billing-with-quotation/apis/invoices/receipt.php"
                        "https://whatsapp.billpro360.com/bill/apis/invoices/receipt.php"
                      )
                    }
                  >
                    Thermal Receipt
                  </Button>

                  <Button
                    disabled={loading}
                    color="secondary"
                    className="w-100"
                    onClick={() =>
                      openPdf(
                        // "http://localhost/billing-with-quotation/apis/invoices/generate.php"
                        // "http://localhost/bill_backend_production/apis/invoices/generate.php"
                        "https://whatsapp.billpro360.com/bill/apis/invoices/generate.php"
                      )
                    }
                  >
                    Invoice PDF
                  </Button>

                </>
              )}

            </CardBody>
          </Card>
        </Col>

        {/* PAYMENT MODAL */}
        <Modal
          isOpen={paymentOpen}
          backdrop="static"
          onOpened={() => {
            setTimeout(() => {
              if (loyaltyBalance > 0) {
                document.getElementById("redeemInput")?.focus();
              } else {
                paymentInputRef.current?.focus();
              }
            }, 150);
          }}
        >
          {/* Dark Teal Header like your design */}
          <ModalHeader
            className="text-white fw-bold text-uppercase"
            style={{ backgroundColor: '#108d83', borderBottom: 'none' }}
          >
            {paymentId ? "Update Payment" : "Payment"}
          </ModalHeader>

          <ModalBody className="p-4">
            {/* Bill Summary Section - Modern Card Style */}
            <div className="mb-4 bg-light p-3 rounded border shadow-sm">
              {cart?.map(i => (
                <div key={i.key} className="d-flex justify-content-between small mb-1 text-secondary">
                  <div>{textCapitalize(i.name)} × {i.qty}</div>
                  <div>₹ {(i.price * i.qty).toFixed(2)}</div>
                </div>
              ))}
              <hr className="my-2" />

              <div className="d-flex justify-content-between fw-bold text-dark mb-1">
                <span>Subtotal</span>
                <span>₹ {Number(cartSubtotal).toFixed(2)}</span>
              </div>

              <div className="d-flex justify-content-between small text-muted">
                <span>CGST</span>
                <span>₹ {cgstTotal.toFixed(2)}</span>
              </div>
              <div className="d-flex justify-content-between small text-muted mb-2">
                <span>SGST</span>
                <span>₹ {sgstTotal.toFixed(2)}</span>
              </div>

              {/* 🔥 Real-time Discount Summary Logic */}
              {(discountValue > 0 || redeemPoints > 0) && (
                <div className="mt-2 pt-2 border-top">
                  {discountValue > 0 && (
                    <div className="d-flex justify-content-between text-danger small">
                      <span>Manual Discount {discountType === "PERCENTAGE" ? `(${discountValue}%)` : ""}</span>
                      <span>- ₹{calculatedManualDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  {redeemPoints > 0 && (
                    <div className="d-flex justify-content-between text-success small">
                      <span>Redeem Value (Points)</span>
                      <span>- ₹{Number(redeemPoints).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="d-flex justify-content-between border-top pt-2 mt-2 align-items-center">
                <span className="fw-bold fs-5 text-dark">Grand Total</span>
                <span className="fw-bold fs-4" style={{ color: '#0d6efd' }}>
                  ₹ {Number(grandTotal).toFixed(2)}
                </span>
              </div>
            </div>

            {/* Loyalty Points Section */}
            {loyaltyBalance > 0 && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="small fw-bold text-muted">Available Loyalty Points: <b>{loyaltyBalance}</b></label>
                  <Badge color="success" pill>Redeemable</Badge>
                </div>
                <Input
                  id="redeemInput"
                  type="number"
                  min={0}
                  max={loyaltyBalance}
                  placeholder="Enter points"
                  value={redeemPoints}
                  className="border-success"
                  onChange={(e) => setRedeemPoints(Math.min(Number(e.target.value || 0), loyaltyBalance))}
                />
              </div>
            )}

            {/* 🔥 Manual Discount Section with Addon Toggle */}
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="small fw-bold text-muted">Manual Discount</label>
                <div className="btn-group btn-group-sm shadow-sm" style={{ borderRadius: '5px', overflow: 'hidden' }}>
                  <Button
                    type="button"
                    color={discountType === "FLAT" ? "dark" : "outline-dark"}
                    onClick={() => { setDiscountType("FLAT"); setDiscountValue(0); }}
                    style={{ width: '40px', fontSize: '12px' }}
                  >₹</Button>
                  <Button
                    type="button"
                    color={discountType === "PERCENTAGE" ? "dark" : "outline-dark"}
                    onClick={() => { setDiscountType("PERCENTAGE"); setDiscountValue(0); }}
                    style={{ width: '40px', fontSize: '12px' }}
                  >%</Button>
                </div>
              </div>
              <Input
                innerRef={paymentInputRef}
                type="number"
                min={0}
                max={discountType === "PERCENTAGE" ? 100 : cartSubtotal}
                placeholder={discountType === "PERCENTAGE" ? "Enter %" : "Enter Amount"}
                value={discountValue}
                className="fw-bold border-secondary"
                onChange={e => {
                  let val = Number(e.target.value || 0);
                  let maxLimit = discountType === "PERCENTAGE" ? 100 : cartSubtotal;
                  setDiscountValue(Math.min(val, maxLimit));
                }}
              />
            </div>

            {/* Payment Mode Selection */}
            <div className="mb-2">
              <label className="small fw-bold text-muted text-uppercase">Payment Mode</label>
              <Input
                type="select"
                className="form-select fw-bold border-primary text-primary"
                value={paymentMode}
                onChange={e => setPaymentMode(e.target.value)}
              >
                <option value="CASH">Cash (1)</option>
                <option value="UPI">UPI (2)</option>
                <option value="CARD">Card (3)</option>
              </Input>
            </div>
          </ModalBody>

          {/* Buttons centered as per your design */}
          <ModalFooter className="justify-content-center border-0 pb-4">
            <Button
              disabled={loading}
              style={{ backgroundColor: '#108d83', border: 'none', minWidth: '160px' }}
              className="fw-bold py-2 shadow-sm rounded-pill"
              onClick={createPayment}
            >
              Confirm Payment
            </Button>
            <Button
              disabled={loading}
              color="danger"
              className="fw-bold py-2 shadow-sm rounded-pill"
              style={{ minWidth: '160px' }}
              onClick={() => { setPaymentOpen(false); reloadWindow(); }}
            >
              Cancel & New Sale
            </Button>
          </ModalFooter>
        </Modal>

      </Row>
    </>
  );
};

export default POS;
